import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAppContext } from '../context/AppContext';
import { translations } from '../translations/translations';
import SectionWrapper, { SectionHeader } from '../components/SectionWrapper';
import ProjectCard from '../components/ProjectCard';
import ProjectModal from '../components/ProjectModal';
import { projectGridReveal } from '../motion/presets';
import '../styles/Projects.css';

const projectList = [
  {
    id: 7,
    titleTr: 'Moo-tion',
    titleEn: 'Moo-tion',
    featured: true,
    labelTr: 'Gerçek Zamanlı AI Sistemi',
    labelEn: 'Real-time AI System',
    impactTr: 'YOLO tabanlı RTSP pipeline ile ineklerde kızgınlık belirtilerini gerçek zamanlı izleyen akıllı çiftlik sistemi.',
    impactEn: 'A smart-farm monitoring system using a YOLO-based RTSP pipeline to detect estrus-related cow behavior in real time.',
    videoLink: 'https://www.youtube.com/watch?v=uhvhUnFRo8I',
    websiteLink: 'https://moo-tion.github.io/',
    tags: ['YOLO', 'Computer Vision', 'RTSP'],
    details: {
      problemTr: 'Süt çiftliklerinde kızgınlık dönemini zamanında fark etmek zor olabilir; manuel gözlem hem yorucu hem de kaçırmaya açıktır.',
      problemEn: 'Detecting estrus on dairy farms at the right time can be difficult; manual observation is labor-intensive and easy to miss.',
      roleTr: 'Bilgisayarlı görü tabanlı izleme sisteminin modelleme, gerçek zamanlı akış işleme ve tespit mantığı üzerinde çalıştım.',
      roleEn: 'Worked on the computer-vision monitoring system, real-time stream processing, and detection logic.',
      approachTr: 'RTSP kamera akışlarını gerçek zamanlı işleyerek YOLO ağırlıklı bilgisayarlı görü algoritmaları ile inekleri ve davranış sinyallerini izleyen bir pipeline kurduk.',
      approachEn: 'Built a real-time pipeline that processes RTSP camera streams and uses YOLO-focused computer vision algorithms to monitor cows and behavioral signals.',
      highlightsTr: ['RTSP kamera akışı ile gerçek zamanlı çalışma', 'YOLO tabanlı nesne tespiti', 'Kızgınlık belirtisi olabilecek davranışların akıllı izlenmesi'],
      highlightsEn: ['Real-time processing from RTSP camera streams', 'YOLO-based object detection', 'Smart monitoring of behavioral signals related to estrus'],
      resultsTr: 'Çiftliklerde sürekli izleme sağlayabilecek, kızgınlık tespitini daha erken ve sistematik hale getirmeyi hedefleyen bir prototip geliştirildi.',
      resultsEn: 'Developed a prototype aimed at continuous farm monitoring and earlier, more systematic estrus detection.',
    },
  },
  {
    id: 1,
    titleTr: 'Bilconnect Backend Geliştirme',
    titleEn: 'Bilconnect Backend Development',
    labelTr: 'Backend Sistemi',
    labelEn: 'Backend System',
    impactTr: 'Firebase ve Java ile gerçek zamanlı veri senkronizasyonu ve güvenli kimlik doğrulama altyapısı.',
    impactEn: 'Firebase and Java backend with real-time data sync and secure authentication infrastructure.',
    repoLink: 'https://github.com/Mertozkaya21/Bilconnect',
    tags: ['Firebase', 'Java', 'Backend'],
    details: {
      problemTr: 'Bilconnect platformu için ölçeklenebilir bir backend altyapısı gerekiyordu.',
      problemEn: 'Bilconnect needed a scalable backend infrastructure for user management.',
      roleTr: 'Backend geliştirici olarak tüm sunucu tarafı mimariyi tasarlayıp kodladım.',
      roleEn: 'Backend developer — designed and implemented all server-side architecture.',
      approachTr: 'Firebase Realtime DB ve Authentication kullanarak mikro-servis benzeri modüler yapı oluşturdum.',
      approachEn: 'Built modular, microservice-inspired architecture using Firebase Realtime DB and Auth.',
      highlightsTr: ['Firebase Authentication entegrasyonu', 'Gerçek zamanlı veri senkronizasyonu', 'Modüler servis katmanı'],
      highlightsEn: ['Firebase Authentication integration', 'Real-time data synchronization', 'Modular service layer architecture'],
      resultsTr: 'Güvenli ve ölçeklenebilir kullanıcı yönetim sistemi başarıyla tamamlandı.',
      resultsEn: 'Successfully shipped a secure, scalable user management system.',
    },
  },
  {
    id: 2,
    titleTr: 'Unity Projeleri',
    titleEn: 'Unity Game Projects',
    labelTr: 'Oyun Mekanikleri',
    labelEn: 'Game Mechanics',
    impactTr: 'Object Pooling, Coroutines ve Game Manager pattern ile profesyonel oyun mekanikleri.',
    impactEn: 'Professional game mechanics using Object Pooling, Coroutines & Game Manager patterns.',
    repoLink: 'https://youtu.be/Gc6-w1v-_yk',
    tags: ['Unity', 'C#', 'Game Dev'],
    details: {
      problemTr: 'Farklı oyun mekaniklerini öğrenmek ve uygulamak.',
      problemEn: 'Learning and implementing different game mechanics and design patterns.',
      roleTr: 'Tek geliştirici — tüm oyun tasarımı ve kodlamasını yaptım.',
      roleEn: 'Solo developer — handled all game design, programming, and testing.',
      approachTr: 'Her projede farklı bir Unity design pattern kullanarak iteratif geliştirme yaptım.',
      approachEn: 'Iterative development using different Unity design patterns per project.',
      highlightsTr: ['Object Pooling ile performans optimizasyonu', 'Scene management ve data persistence', 'Coroutine-based animation sistemi'],
      highlightsEn: ['Performance optimization via Object Pooling', 'Scene management & data persistence', 'Coroutine-based animation system'],
      resultsTr: 'Unity mekaniklerinde kapsamlı deneyim kazandım.',
      resultsEn: 'Gained comprehensive experience across Unity mechanics and patterns.',
    },
  },
  {
    id: 3,
    titleTr: 'UART Protokolü (Basys3)',
    titleEn: 'UART Protocol (Basys3 FPGA)',
    labelTr: 'Gömülü Sistem',
    labelEn: 'Embedded System',
    impactTr: 'İki FPGA kartı arasında güvenilir seri veri iletişimi.',
    impactEn: 'Reliable serial data communication between two FPGA boards.',
    repoLink: 'https://github.com/Mertozkaya21/UART-protocol',
    tags: ['FPGA', 'Verilog', 'Embedded'],
    details: {
      problemTr: 'İki FPGA kartı arasında düşük seviyede veri iletişimi sağlamak.',
      problemEn: 'Implementing low-level data communication between two FPGA boards.',
      roleTr: 'Donanım tasarımcısı — UART TX/RX modüllerini tasarlayıp sentezledim.',
      roleEn: 'Hardware designer — designed and synthesized UART TX/RX modules.',
      approachTr: 'FSM tabanlı UART modülleri ile baud rate generation ve error detection uyguladım.',
      approachEn: 'FSM-based UART modules with baud rate generation and error detection.',
      highlightsTr: ['Custom baud rate generator', 'Error detection ve parity checking', 'Hardware-level debugging'],
      highlightsEn: ['Custom baud rate generator', 'Error detection & parity checking', 'Hardware-level debugging with ILA'],
      resultsTr: 'Kararlı çift yönlü UART iletişimi başarıyla gerçekleştirildi.',
      resultsEn: 'Achieved stable bidirectional UART communication at target baud rate.',
    },
  },
  {
    id: 4,
    titleTr: 'Bilkent Tanıtım Ofisi Backend',
    titleEn: 'Bilkent Promotion Office Backend',
    labelTr: 'REST API',
    labelEn: 'REST API',
    impactTr: 'Spring Boot ile RESTful API ve SQL veritabanı tasarımı.',
    impactEn: 'RESTful API design with Spring Boot and SQL database architecture.',
    repoLink: 'https://github.com/Mertozkaya21/CS-319-Project',
    tags: ['Spring Boot', 'SQL', 'REST'],
    details: {
      problemTr: 'Üniversite tanıtım süreçlerini yönetecek bir web uygulaması backend\'i gerekiyordu.',
      problemEn: 'Needed a web application backend to manage university promotion workflows.',
      roleTr: 'Backend geliştirici — RESTful servisler ve veritabanı tasarımını üstlendim.',
      roleEn: 'Backend developer — owned RESTful services and database design.',
      approachTr: 'Spring Boot ile katmanlı mimari, JPA/Hibernate ile ORM, SQL ile ilişkisel veritabanı tasarımı.',
      approachEn: 'Layered architecture with Spring Boot, ORM via JPA/Hibernate, relational DB design.',
      highlightsTr: ['RESTful API tasarımı', 'JPA/Hibernate ORM entegrasyonu', 'İlişkisel veritabanı normalizasyonu'],
      highlightsEn: ['RESTful API design', 'JPA/Hibernate ORM integration', 'Relational database normalization'],
      resultsTr: 'Tam fonksiyonel backend başarıyla teslim edildi.',
      resultsEn: 'Delivered a fully functional, well-documented backend.',
    },
  },
  {
    id: 5,
    titleTr: 'Yaş Tahmini — Transfer Learning',
    titleEn: 'Age Estimation — Transfer Learning',
    labelTr: 'ML Araştırması',
    labelEn: 'ML Research',
    impactTr: 'CNN mimarileri kıyaslaması ile UTKFace üzerinde yaş tahmini.',
    impactEn: 'Age estimation on UTKFace dataset comparing CNN architectures via Transfer Learning.',
    tags: ['CNN', 'ResNet', 'TensorFlow'],
    details: {
      problemTr: 'Yüz görüntülerinden yaş tahmini yapan bir model geliştirmek.',
      problemEn: 'Develop an accurate age estimation model from facial images.',
      roleTr: 'ML araştırmacısı — model seçimi, eğitim ve kıyaslama analizlerini yaptım.',
      roleEn: 'ML researcher — model selection, training pipeline, and comparative analysis.',
      approachTr: 'UTKFace veri seti üzerinde ResNet, VGG ve EfficientNet mimarilerini transfer learning ile kıyasladım.',
      approachEn: 'Compared ResNet, VGG, and EfficientNet via Transfer Learning on UTKFace dataset.',
      highlightsTr: ['Çoklu CNN mimari kıyaslaması', 'Data augmentation pipeline', 'MAE metriği ile performans optimizasyonu'],
      highlightsEn: ['Multi-architecture CNN comparison', 'Data augmentation pipeline', 'Performance optimization via MAE metric'],
      resultsTr: 'En iyi model ile rekabetçi MAE skoru elde edildi.',
      resultsEn: 'Achieved competitive MAE scores with the best-performing architecture.',
    },
  },
  {
    id: 6,
    titleTr: 'NLP Bias Tespiti',
    titleEn: 'NLP Bias Detection',
    labelTr: 'NLP Analizi',
    labelEn: 'NLP Analysis',
    impactTr: 'N-gram, KL-diverjans ve LLM-as-Judge ile metin önyargı analizi.',
    impactEn: 'Text bias analysis using N-gram, KL-divergence, and LLM-as-Judge methods.',
    tags: ['NLP', 'BERT', 'Python'],
    details: {
      problemTr: 'Metin verilerindeki önyargıları sistematik olarak tespit etmek.',
      problemEn: 'Systematically detect and quantify biases in text data.',
      roleTr: 'NLP araştırmacısı — analiz metodolojisini tasarlayıp uyguladım.',
      roleEn: 'NLP researcher — designed and implemented the analysis methodology.',
      approachTr: 'N-gram frekans analizi, Kullback-Leibler diverjansı, JSD ve LLM-as-Judge yöntemlerini birleştirdim.',
      approachEn: 'Combined N-gram frequency analysis, KL-divergence, JSD, and LLM-as-Judge approaches.',
      highlightsTr: ['Çoklu bias tespit metodolojisi', 'Bigram ilişki analizi', 'LLM tabanlı değerlendirme sistemi'],
      highlightsEn: ['Multi-method bias detection framework', 'Bigram relationship analysis', 'LLM-based evaluation system'],
      resultsTr: 'Farklı bias türlerini etkili şekilde tespit eden karşılaştırmalı analiz tamamlandı.',
      resultsEn: 'Completed comparative analysis effectively detecting different bias types.',
    },
  },
];

export default function Projects() {
  const [selectedProject, setSelectedProject] = useState(null);
  const { language } = useAppContext();
  const t = translations[language];

  return (
    <>
      <SectionWrapper id="projects" className="projects">
        <SectionHeader
          label={t.projects.label}
          title={t.projects.title}
          subtitle={t.projects.subtitle}
        />

        <motion.div className="projects__grid" variants={projectGridReveal}>
          {projectList.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              language={language}
              t={t}
              onClick={() => setSelectedProject(project)}
            />
          ))}
        </motion.div>
      </SectionWrapper>

      <ProjectModal
        project={selectedProject}
        language={language}
        t={t}
        onClose={() => setSelectedProject(null)}
      />
    </>
  );
}
